// Patient endpoints — 1:1 wrappers around the Withings `program` webservice.
// programid is read per-call from getProgramId() (not closed over) so SPA program switches take effect.

import { ws } from '@/lib/wiservice';
import { apiv2Get } from '@/lib/apiv2';
import { getProgramId } from '@/config/program';
import { measureWindowYmd } from '@/logic/measureWindow';

// Our authoritative patients-table row (Platform API CRUD), distinct from the
// Withings program.getpatient demographics. Used as the source of truth for the
// CMS $align payload (first_name/last_name/date_of_birth/sex).
export interface PatientRecord {
  withings_user_id: string;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null; // YYYY-MM-DD
  sex: string | null; // Male | Female
  phone?: string | null; // authoritative — patients.phone (NOT NULL in schema)
  email?: string | null;
  ehr_patient_id?: number | null;
  // Postal address (no country column — implicit US). Used to pre-fill device shipping.
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  // Additional patients-table columns surfaced read-only in the Patient info modal.
  // Optional — rendered only when apiv2 returns them (an absent column just won't show).
  sms_opt_in?: boolean | null;
  timezone?: string | null;
  program?: string | null;
  pcp_name?: string | null;
  pcp_npi?: string | null;
  pcp_specialty?: string | null;
  pcp_phone?: string | null;
  pcp_address?: string | null;
}

export async function getPatientRecord(userid: string | number): Promise<PatientRecord | null> {
  const rows = await apiv2Get('patients', { withings_user_id: String(userid), limit: 1 });
  const list = Array.isArray(rows) ? (rows as PatientRecord[]) : [];
  return list[0] ?? null;
}

// A device attached to the patient's Withings account (program.getdevicesbypatientid).
// Devices attach automatically once the patient sets one up.
export interface PatientDevice {
  id: number;
  model: number;
  type: number;
  macaddress?: string;
  serialnumber?: string;
  fw?: string;
  batterylvl?: number;
  lastsessiondate?: number;
  lastuseddate?: number;
  externalid?: string | null;
}

export async function getDevicesByPatient(userid: string | number): Promise<PatientDevice[]> {
  const res = await ws<{ devices?: PatientDevice[] }>('program', 'getdevicesbypatientid', {
    programid: getProgramId(),
    userid,
  });
  return res.body?.devices ?? [];
}

export interface PatientRosterRow {
  userid: string | number;
  firstname: string;
  lastname: string;
  phone_number: string | null;
}

export interface PatientDetails {
  userid: string | number;
  firstname?: string;
  lastname?: string;
  phone?: { number?: string | null } | null;
  phone_number?: string | null;
  /** The program category; -2 = archived. */
  category?: number | string | null;
  [key: string]: unknown;
}

export interface MeasureSummary {
  firstSystolic: number | null;
  lastSystolic: number | null;
  lastEpoch: number | null;
  firstDiastolic: number | null;
  lastDiastolic: number | null;
  firstWeight: number | null;
  lastWeight: number | null;
  lastFatRatio: number | null;
  lastMuscleMass: number | null;
}

export interface MeasureReading {
  type: number;
  epoch: number;
  value: number;
  unit: number; // power-of-ten exponent; real value = value * 10^unit (0 for BP, e.g. -3 for weight)
  grpid: number | string;
}

export interface MeasureReadingsResult {
  readings: MeasureReading[];
  total: number;
}

export interface ProgramNoteAuthor {
  userid: number | string;
  firstname?: string;
  lastname?: string;
}

// `list`/`create`/`update` return the author as a nested object (author.userid);
// the id field on the response is `id`, while create/update/delete take `noteid` as input.
export interface ProgramNote {
  id: number | string;
  message: string;
  userid: number | string;
  author?: ProgramNoteAuthor;
  authorid?: number | string;
  programid?: number | string;
  active?: boolean;
  created?: number;
  modified?: number;
  [key: string]: unknown;
}

export interface ProgramNoteListResult {
  notes: ProgramNote[];
  more: boolean;
  offset: number | null;
}

interface ListPatientsBody {
  patients: Array<{
    userid: string | number;
    firstname: string;
    lastname: string;
    phone_number?: string | null;
    phone?: string | null;
  }>;
}

interface GetPatientBody {
  patient: PatientDetails;
}

interface GetMeasureByUserBody {
  measures: Array<{
    type: number;
    epoch: number;
    grpid: number | string;
    data: { value: number; unit?: number };
  }>;
  total?: number;
}

interface VoiceAccessTokenBody {
  access_token: string;
}

interface ProgramNoteCreateBody {
  program_note: ProgramNote;
}

interface ProgramNoteListBody {
  program_notes: ProgramNote[];
  more?: boolean;
  offset?: number | null;
}

// Withings ships `data.value * 10^unit` — normalize to the scalar value.
function normalizeMeasureValue(m: { data: { value: number; unit?: number } }): number {
  return m.data.value * (10 ** (m.data?.unit ?? 0));
}

export async function listPatients(): Promise<PatientRosterRow[]> {
  const response = await ws<ListPatientsBody>('program', 'listpatients', {
    programid: getProgramId(),
    filter: '{}',
    limit: 500,
  });
  return response.body.patients.map((p) => ({
    userid: p.userid,
    firstname: p.firstname,
    lastname: p.lastname,
    phone_number: p.phone_number || p.phone || null,
  }));
}

export async function getPatient(userid: string | number): Promise<PatientDetails> {
  const response = await ws<GetPatientBody>('program', 'getpatient', {
    programid: getProgramId(),
    userid,
  });
  return response.body.patient;
}

// `first*` fields are first-in-fetched-window, NOT a clinical baseline. A baseline that may be
// LABELLED as one comes off the CMS filing — see logic/baseline.ts (care_goal is retired).
const SUMMARY_MEASTYPES = [1, 6, 9, 10, 76] as const;

export async function getMeasuresByUser(
  userid: string | number,
  options: { days?: number } = {},
): Promise<MeasureSummary> {
  const days = options.days ?? 90;
  const { startdateymd, enddateymd } = measureWindowYmd(days);

  // One call PER meastype: BP (9/10) and weight (1/6/76) are separate measure groups, so a
  // combined call paginates the newer series off the capped page and its last*() go stale.
  // allSettled (not all) so one flaky type degrades only its own values, not the whole card.
  const settled = await Promise.allSettled(
    SUMMARY_MEASTYPES.map((t) => ws<GetMeasureByUserBody>('program', 'getmeasurebyuser', {
      programid: getProgramId(),
      userid,
      startdateymd,
      enddateymd,
      meastypes: String(t),
      limit: '250',
      offset: '0',
    })),
  );
  const byType = new Map<number, GetMeasureByUserBody['measures']>();
  SUMMARY_MEASTYPES.forEach((t, i) => {
    const r = settled[i]!;
    byType.set(t, r.status === 'fulfilled' ? (r.value.body.measures || []) : []);
  });

  const sortedOf = (type: number) => (byType.get(type) ?? []).slice().sort((a, b) => a.epoch - b.epoch);
  const last = (type: number): number | null => {
    const m = sortedOf(type);
    return m.length > 0 ? normalizeMeasureValue(m[m.length - 1]!) : null;
  };
  const first = (type: number): number | null => {
    const m = sortedOf(type);
    return m.length > 0 ? normalizeMeasureValue(m[0]!) : null;
  };
  const lastEpochOf = (type: number): number | null => {
    const m = sortedOf(type);
    return m.length > 0 ? m[m.length - 1]!.epoch : null;
  };

  return {
    firstSystolic: first(10),
    lastSystolic: last(10),
    lastEpoch: lastEpochOf(10),
    firstDiastolic: first(9),
    lastDiastolic: last(9),
    firstWeight: first(1),
    lastWeight: last(1),
    lastFatRatio: last(6),
    lastMuscleMass: last(76),
  };
}

export async function getMeasureReadings(
  userid: string | number,
  meastypes: string,
  options: { startdateymd?: string; enddateymd?: string; limit?: number; offset?: number } = {},
): Promise<MeasureReadingsResult> {
  const dft = measureWindowYmd(90);

  const response = await ws<GetMeasureByUserBody>('program', 'getmeasurebyuser', {
    programid: getProgramId(),
    userid,
    meastypes,
    startdateymd: options.startdateymd || dft.startdateymd,
    enddateymd: options.enddateymd || dft.enddateymd,
    limit: String(options.limit || 250),
    offset: String(options.offset || 0),
  });
  const measures = response.body.measures || [];
  return {
    readings: measures.map((m) => ({
      type: m.type,
      epoch: m.epoch,
      value: m.data.value,
      unit: m.data.unit ?? 0,
      grpid: m.grpid,
    })),
    total: response.body.total || measures.length,
  };
}

// Withings only records device measures once the program category is set — until then getmeasurebyuser (the BP chart source) stays empty.
export async function updatePatientCategory(
  userid: string | number,
  category: number = 0,
): Promise<void> {
  await ws('program', 'updatepatient', {
    programid: getProgramId(),
    userid,
    category,
  });
}

// Reversible offboarding (wpc-front parity): stops SMS reminders + data transmission to
// RPM and drops the patient off the program board. Undo = 'unarchivepatient'.
export async function archivePatient(userid: string | number): Promise<void> {
  await ws('program', 'archivepatient', {
    programid: getProgramId(),
    userid,
  });
}

// Recomputes the category; readings taken while archived do not come back to the charts.
export async function unarchivePatient(userid: string | number): Promise<void> {
  await ws('program', 'unarchivepatient', {
    programid: getProgramId(),
    userid,
  });
}

export async function getVoiceAccessToken(): Promise<string> {
  const response = await ws<VoiceAccessTokenBody>('program', 'getvoiceaccesstoken', {
    programid: getProgramId(),
  });
  return response.body.access_token;
}

export async function createNote(
  userid: string | number,
  message: string,
  authorid: string | number,
): Promise<ProgramNote> {
  // `create` expects the body field `note`; `update` expects `message` (asymmetric scalews contract).
  const response = await ws<ProgramNoteCreateBody>('programnote', 'create', {
    programid: getProgramId(),
    userid: String(userid),
    note: message,
    authorid: String(authorid),
    mentions: [],
  });
  return response.body.program_note;
}

// Server returns newest-first; the default call (no limit) returns every note.
export async function listNotes(
  userid: string | number,
  options: { limit?: number; offset?: number } = {},
): Promise<ProgramNoteListResult> {
  const params: Record<string, unknown> = {
    programid: getProgramId(),
    userid: String(userid),
  };
  if (options.limit != null) params.limit = String(options.limit);
  if (options.offset != null) params.offset = String(options.offset);
  const response = await ws<ProgramNoteListBody>('programnote', 'list', params);
  return {
    notes: response.body.program_notes || [],
    more: response.body.more ?? false,
    offset: response.body.offset ?? null,
  };
}

export async function updateNote(
  noteid: string | number,
  userid: string | number,
  message: string,
  authorid: string | number,
): Promise<ProgramNote> {
  const response = await ws<ProgramNoteCreateBody>('programnote', 'update', {
    programid: getProgramId(),
    noteid: String(noteid),
    userid: String(userid),
    message,
    authorid: String(authorid),
  });
  return response.body.program_note;
}

export async function deleteNote(
  noteid: string | number,
  userid: string | number,
): Promise<void> {
  await ws('programnote', 'delete', {
    programid: getProgramId(),
    noteid: String(noteid),
    userid: String(userid),
  });
}
